import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useToggle from '../hooks/useToggle';
import Page from '../components/Page';
import VideoModel from '../components/models/VideoModel';
import { AppColors, config, typographyClamps } from '../utils/utils';
import { parseCurrencyString } from '../utils/formateCarPrice';
import { encryptString, getAssetURL } from '../utils/url';
import { CarAvailability } from '../enum';
import { Cars } from '../store/cars/api/interface';
import { useAppDispatch, useAppSelector } from '../store';
import { findCar } from '../store/cars/api';
import { useNavigate } from 'react-router-dom';
import ImageSlider from '../components/ImageSlider';
import { toast } from 'react-toastify';
import { IsNotNullOrEmpty } from '../utils/valid';
import { getDealer } from '../store/dealers/api';

interface Vehicle {
  imagePath: string;
}

const vehicleList: Vehicle[] = [
  { imagePath: '/static/home_page/sterling_cadillac/img_vehicle_1.png' },
  { imagePath: '/static/home_page/sterling_cadillac/img_vehicle_2.png' },
  { imagePath: '/static/home_page/sterling_cadillac/img_vehicle_3.png' },
  { imagePath: '/static/home_page/sterling_cadillac/img_vehicle_4.png' },
  { imagePath: '/static/home_page/sterling_cadillac/img_vehicle_5.png' },
];

export interface ScreenZone {
  image: string;
  key: string;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

// ── 3 Footer Cards ──
const ZONES_FOOTER_CARDS: ScreenZone[] = [
  {
    key: 'view_inventory',
    image:
      '/static/home_page/sterling_cadillac/img_card_certified_pre_owned.png',
    minX: 0.0,
    maxX: 0.33,
    minY: 0,
    maxY: 1,
  },
  {
    key: 'apply_now',
    image: '/static/home_page/sterling_cadillac/img_card_get_approved.png',
    minX: 0.33,
    maxX: 0.66,
    minY: 0,
    maxY: 1,
  },
  {
    key: 'value_trade_in',
    image: '/static/home_page/sterling_cadillac/img_card_value_trade_in.png',
    minX: 0.66,
    maxX: 1.0,
    minY: 0,
    maxY: 1,
  },
];

const Home: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    // data, 
    isLoading,
    status
  } = useAppSelector((state) => state.cars);
  const {
    dealer: dealerData,
    // isLoading: isLoadingDealerSettings,
    status: dealerSettingsStatus,
  } = useAppSelector((state) => state.dealers);

  const [videoUrl, setVideoUrl] = useState<string | undefined>();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { toggle, onOpen, onClose } = useToggle();

  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md')); // if screen <= 'ms' for tablet and mobile device

  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    const scrollAmount = 300; // adjust as needed
    const container = scrollRef.current;

    if (!container) return;

    const maxScrollLeft = 0;
    const maxScrollRight = container.scrollWidth - container.clientWidth;

    if (direction === 'left') {
      // Only scroll left if not already at the start
      if (container.scrollLeft > maxScrollLeft) {
        const newScrollLeft = Math.max(
          container.scrollLeft - scrollAmount,
          maxScrollLeft,
        );
        container.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth',
        });
      }
    } else if (direction === 'right') {
      // Only scroll right if not already at the end
      if (container.scrollLeft < maxScrollRight) {
        const newScrollLeft = Math.min(
          container.scrollLeft + scrollAmount,
          maxScrollRight,
        );
        container.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth',
        });
      }
    }
  };

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findCar({
          dealerId: config.DEALER_ID,
          limit: 4,
        }),
      );
    }
  }, [dispatch, status]);

  useEffect(() => {
    if (dealerSettingsStatus === 'idle') {
      dispatch(
        getDealer({
          id: config.DEALER_ID,
        }),
      );
    }
  }, [dispatch, dealerSettingsStatus]);

  useEffect(() => {
    if (toggle === false) {
      setVideoUrl(undefined);
    }
  }, [toggle]);

  useEffect(() => {
    if (window?.MilesAIWidgetInit) {
      window?.MilesAIWidgetInit({});
    }
  }, [])

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const carItemView = (item: Cars) => {
    const { symbolOrCode, amount } = parseCurrencyString(item?.price);

    return (
      <Card
        sx={{
          minWidth: 300,

          borderRadius: '16px',
          padding: '12px',
          margin: 1,
          backgroundColor: '#FFFFFF',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          boxShadow: '0 0 15px 0px rgba(0, 0, 0, 0.15)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
          },
        }}
        // onClick={() => handleCardClick(item, isScan)}
        onClick={() => {
          navigate(`/info/${encryptString(item.id)}`);
          // window.open(item?.link, '_blank');
        }}
        // onClick={() => {
        //   window.open(item?.link, '_blank');
        // }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '200px',
            borderRadius: '6px',
            overflow: 'hidden',
          }}
        >
          {/* Image */}
          <Box
            component='img'
            src={
              item?.photos[0]?.key
                ? getAssetURL(item?.photos[0]?.key, item?.photos[0]?.url)
                : item?.photos[0]?.url
                  ? item?.photos[0]?.url
                  : '/static/car_placeholder.png'
            }
            alt={item?.modelCode || 'Car Image'}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              opacity:
                item?.availability !== CarAvailability.available ? 0.5 : 1,
            }}
          />

          {item?.availability !== CarAvailability.available && (
            <Box
              component='img'
              src='/static/sold_out.png'
              alt='Sold Out'
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: '80%',
                maxHeight: '80%',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            />
          )}

          {/* Favorite Icon */}
          {/* <IconButton
            aria-label='favorite'
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(item);
            }}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              backgroundColor: 'rgba(255,255,255,0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)',
              },
              boxShadow: 1,
              borderRadius: '50%',
              padding: '4px',
            }}
          >
            {isFavorite ? (
              <FavoriteRoundedIcon sx={{ color: 'red', fontSize: '20px' }} />
            ) : (
              <FavoriteBorderRoundedIcon
                sx={{ color: '#666', fontSize: '20px' }}
              />
            )}
          </IconButton> */}
        </Box>
        <Divider sx={{ my: 1, mt: 1 }} />
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          {/* Left box: MRP and Price stacked vertically */}
          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                color: 'grey.600',
                fontWeight: 500,
                mt: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px',
              }}
            >
              {item?.dealerName}
            </Typography>
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 600,
                color: '#000',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '190px',
              }}
            >
              {item?.title}
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                color: 'grey.600',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '200px',
              }}
            >
              {[
                item?.driveType,
                item?.car_generation?.title,
                item?.mileage,
                item?.engine,
              ]
                .filter(Boolean)
                .join(' • ')}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: 'grey.600',
              }}
            >
              MRP
            </Typography>
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 600,
                background: (theme: any) => theme.palette.gradients.primary,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                mt: 0,
              }}
            >
              {symbolOrCode}
              {amount}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  const handleViewInventory = () => {
    if (window.MilesAIWidget?.showInventory) {
      window.MilesAIWidget?.showInventory();
    }
  }

  const buildClickHandler =
    (zones: ScreenZone[]) => (e: React.MouseEvent<HTMLImageElement>) => {
      const img = e.currentTarget as HTMLImageElement;
      const rect = img.getBoundingClientRect();
      const rx = (e.clientX - rect.left) / rect.width;
      const ry = (e.clientY - rect.top) / rect.height;

      const matched = zones.find(
        (z) => rx >= z.minX && rx < z.maxX && ry >= z.minY && ry < z.maxY,
      );
      if (matched) footerCardClickHandler(matched.key);
    };

  const footerCardClickHandler = (key: string) => {
    switch (key) {
      case 'view_inventory':
        handleViewInventory();
        break;
      case 'apply_now':
        if (IsNotNullOrEmpty(dealerData?.dealer_settings?.financeAppLink)) {
          window.open(dealerData?.dealer_settings?.financeAppLink, '_blank');
        } else {
          toast.error(
            'The finance application link has not been added by the dealer.',
          );
        }
        break;
      case 'value_trade_in':
        break;
    }
  };

  return (
    <>
      <Page title={'Home'}>
        {/* <Box
          display={'flex'}
          justifyContent={'center'}
        >
          <Box
            component={'img'}
            src='/static/home_page/img_brand_logos.png'
            sx={{ width: { xs: '100%', sm: '70%' } }}
          />
        </Box> */}
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, md: 5 },
            backgroundImage: `url('/static/home_page/sterling_cadillac/img_sterling_cadillac_banner.png')`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: 'min(1120px, 100%)' },
            }}
          >
            <div
              id="qrbuddy-inventory-root"
              data-widget-type="search"
              data-new-inventory-url="/new-inventory"
              data-pre-owned-inventory-url="/pre-owned-inventory"
            ></div>
          </Box>
          {/* <Box
            sx={{
              height: '100%',
              width: '100%',
              flexDirection: 'column',
              alignItems: 'center',
              justifyItems: 'center',
              p: 1,
              display: { xs: 'flex' },
            }}
          >
            <Typography
              sx={{
                fontSize: typographyClamps.h2,
                fontFamily: 'Poppins',
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              Luxury Without Limits
            </Typography>
            <Button
              // variant='contained'
              sx={{
                borderRadius: 0,
                backgroundColor: AppColors.primaryColor,
                color: 'white',
                // width: '120px',
                textTransform: 'none',
                // p: 0.2,
                // px: 1,
                // mt: 1,
                padding: '2px 26px',
                fontSize: typographyClamps.h6,
                whiteSpace: 'pre-line',
                lineHeight: 1.35,
                '&:hover': {
                  borderRadius: 0,
                },
              }}
              onClick={() => {
                // navigate('/inventory');
                navigate('/inventory', {
                  state: { scrollToInventoryHeader: true },
                });
              }}
            >
              {'View\nInventory'}
            </Button>
          </Box> */}
        </Box>

        <Grid
          container
          sx={{
            py: 4,
            px: '10%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Grid size={{ xs: 12, sm: 9 }}>
            <Stack
              sx={{
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  // fontSize: typographyClamps.fontSize20,
                  fontSize: typographyClamps.h5,
                  fontWeight: 800,
                }}
              >
                Elite Vehicles. Exceptional Standards
              </Typography>
              <Typography
                sx={{
                  // fontSize: typographyClamps.fontSize16,
                  fontSize: typographyClamps.h6,
                  fontWeight: 600,
                  color: 'gray',
                }}
              >
                From luxury SUVs to high-performance sedans, every vehicle is
                carefully selected, fully inspected, and ready to deliver a
                driving experience you can trust.
              </Typography>
            </Stack>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 1.5 }}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'flex-end' },
            }}
          >
            <Box
              component={'img'}
              src='/static/home_page/img_carfax.png'
              sx={{
                maxWidth: '100%',
                display: 'block',
              }}
            />
          </Grid>
        </Grid>

        {isLoading ? (
          <center>
            <CircularProgress />{' '}
          </center>
        ) : isMobileOrTablet ? (
          <Box
            sx={{
              maxWidth: '100vw',
              overflow: 'hidden',
              mt: 1,
              mb: 1,
              position: 'relative',
            }}
          >
            {/* Left Arrow */}
            <IconButton
              onClick={() => scroll('left')}
              // disableRipple={true}
              sx={{
                position: 'absolute',
                top: '50%',
                left: 5,
                transform: 'translateY(-50%)',
                zIndex: 1,
                backgroundColor: 'rgba(0,0,0,0.4)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.4)',
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>

            <Stack
              ref={scrollRef}
              flexDirection={'row'}
              sx={{
                overflowX: 'auto',
                scrollBehavior: 'smooth',
                px: 4, // padding so arrows don’t overlap cards
              }}
            >
              {vehicleList.map((card, idx) => (
                <Box
                  key={`vehicle-item-${idx}`}
                  sx={{
                    display: 'flex',
                    maxWidth: { xs: '85%', sm: '60%' }, // Make each card take 80% of viewport width
                    flexShrink: 0, // Prevent shrinking
                    flexGrow: 1,
                  }}
                >
                  <Box component={'img'} src={card.imagePath} />
                </Box>
              ))}
              {/* {data.map(item => {
                return carItemView(item);
              })} */}
            </Stack>

            {/* Right Arrow */}
            <IconButton
              onClick={() => scroll('right')}
              // disableRipple={true}
              sx={{
                position: 'absolute',
                top: '50%',
                right: 5,
                transform: 'translateY(-50%)',
                zIndex: 1,
                backgroundColor: 'rgba(0,0,0,0.4)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.4)',
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        ) : (
          <Grid
            container
            justifyContent={'center'}
            spacing={1}
            sx={{ mb: 5 }}
            // wrap='nowrap'
            // sx={{ overflowX: 'auto' }}
          >
            {vehicleList.map((card, idx) => {
              return (
                <Grid key={`vehicle-item-${idx}`} sx={{ display: 'flex' }}>
                  <Box
                    component={'img'}
                    src={card.imagePath}
                    sx={{ width: '100%' }}
                  />
                </Grid>
              );
            })}
            {/* {data.map((item, idx) => {
              return (
                <Grid
                  key={`vehicle-item-${idx}`}
                  sx={{ display: 'flex' }}
                >
                  {carItemView(item)}
                </Grid>
              );
            })} */}
          </Grid>
          // <Box
          //   sx={{
          //     display: 'flex',
          //     justifyContent: 'center',
          //     alignItems: 'center',
          //     flexWrap: 'nowrap',
          //     overflowX: 'auto',
          //   }}
          // >
          //   {/* {vehicleList.map((card, idx) => (
          //     <Box key={idx}>
          //       <Box
          //         component='img'
          //         src={card.imagePath}
          //         sx={{ width: '100%' }}
          //       />
          //     </Box>
          //   ))} */}
          //   {data.map((item) => {
          //       return carItemView(item);
          //     })}
          // </Box>
        )}

        <Stack
          sx={{
            p: { xs: 1, sm: 3 },
            bgcolor: 'black',
            alignItems: 'center',
            textAlign: 'center',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: typographyClamps.h5,
              fontWeight: 800,
              color: 'white',
            }}
          >
            Luxury Within Reach. Performance Without Limits.
          </Typography>
          <Typography
            sx={{
              fontSize: typographyClamps.h6,
              fontWeight: 600,
              color: 'white',
            }}
          >
            Explore limited-time Cadillac offers featuring low APR, aggressive
            lease payments, and exclusive incentives across our 2025–2026
            lineup.
          </Typography>

          <Button
            // variant='contained'
            sx={{
              borderRadius: 1,
              backgroundColor: AppColors.whiteColor,
              // width: isMobile ? '120px' : '200px',
              width: 'fit-content',
              textTransform: 'none',
              my: 3,
              mx: 0,
              px: 2,
              fontSize: typographyClamps.fontSize15,
              fontWeight: 400,
              whiteSpace: 'pre-line', // This allows text to break at newline characters
            }}
            onClick={() => {
              // navigate('/inventory');
            }}
          >
            Check Financing Options
          </Button>

          <ImageSlider />
        </Stack>

        <Grid
          container
          sx={{
            backgroundColor: 'black',
            p: { xs: 0, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            // mt: 2,
          }}
        >
          <Grid
            size={{ xs: 12, sm: 5.8 }}
            display={{ xs: 'none', sm: 'block' }}
          >
            <Box
              component={'img'}
              src='/static/home_page/sterling_cadillac/img_cadillac_car.png'
              sx={{ height: '100%', width: '100%', objectFit: 'cover' }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 5.8 }}>
            <Box
              sx={{
                background: {
                  xs: `url('/static/home_page/sterling_cadillac/img_cadillac_car.png')`,
                  sm: 'none',
                },
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                position: 'relative',
                overflow: 'clip',
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#000000CC',
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  display: { xs: 'block', sm: 'none' },
                }}
              />

              <Stack
                sx={{
                  color: 'white',
                  p: '10%',
                  position: 'relative',
                }}
              >
                <Typography
                  sx={{
                    fontSize: typographyClamps.h5,
                    fontWeight: 700,
                    letterSpacing: 4,
                    lineHeight: 1.7,
                  }}
                >
                  WELCOME TO <br />
                  Sterling Cadillac
                </Typography>
                <Typography
                  sx={{
                    fontSize: typographyClamps.fontSize16,
                    lineHeight: 1.6,
                    mt: 2,
                  }}
                >
                  Sterling Auto Group is your premier destination for the latest
                  in luxury innovation, including the all-new 2026 Cadillac
                  Escalade Electric in Orlando, Florida. We offer an exclusive
                  selection of high-end vehicles featuring cutting- edge
                  performance, advanced technology, and elite design. Our
                  commitment to excellence makes us your trusted source for your
                  next generation luxury vehicle. We leverage our extensive
                  market knowledge to deliver an elevated buying experience and
                  unmatched value to our clients. We welcome you to our showroom
                  by appointment and look forward to exceeding your
                  expectations!
                </Typography>
                <Typography
                  sx={{
                    fontSize: typographyClamps.h5,
                    fontWeight: 700,
                    letterSpacing: 4,
                    lineHeight: 1.7,
                    mt: 8,
                  }}
                >
                  DRIVEN BY INNOVATION.
                </Typography>
                <Typography
                  sx={{
                    fontSize: typographyClamps.fontSize16,
                    lineHeight: 1.6,
                    mt: 2,
                  }}
                >
                  Start exploring the future of luxury with the 2026 Cadillac
                  Escalade Electric and our premium vehicle lineup in Orlando,
                  FL. If you’re looking to sell your vehicle for cash or trade
                  it in, check our CAR VALUATION to see if your vehicle
                  qualifies for our certified program.
                </Typography>
              </Stack>
            </Box>
          </Grid>
        </Grid>

        <Stack
          sx={{
            bgcolor: 'black',
            alignItems: 'center',
            mx: 'auto',
            p: { xs: 1, sm: 2 },
          }}
        >
          <Button
            // variant='contained'
            sx={{
              borderRadius: 1,
              backgroundColor: AppColors.whiteColor,
              // width: isMobile ? '120px' : '200px',
              width: 'fit-content',
              textTransform: 'none',
              my: 3,
              mx: 0,
              px: { xs: 2, sm: 4 },
              fontSize: typographyClamps.h5,
              fontWeight: 400,
              whiteSpace: 'pre-line', // This allows text to break at newline characters
            }}
            onClick={() => {
              // navigate('/inventory');
              handleViewInventory();
            }}
          >
            View Inventory
          </Button>
        </Stack>

        <Box
          component={'img'}
          src='/static/home_page/sterling_cadillac/img_footer_cards.png'
          width={'100%'}
          sx={{ cursor: 'pointer', display: { xs: 'none', sm: 'block' } }} // ← visual hint that it's clickable
          onClick={buildClickHandler(ZONES_FOOTER_CARDS)}
        />

        <Grid
          container
          gap={{ xs: 1, sm: 2 }}
          justifyContent={'center'}
          display={{ xs: 'block', sm: 'none' }}
        >
          {ZONES_FOOTER_CARDS.map((item) => {
            return (
              <Grid
                size={{ xs: 12, sm: 3.7, md: 1.8 }}
                key={item.key}
                display={'flex'}
              >
                <Box
                  component={'img'}
                  src={item.image}
                  sx={{
                    width: '100%',
                    // borderRadius: 2,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    footerCardClickHandler(item.key);
                  }}
                />
              </Grid>
            );
          })}
        </Grid>

        <Stack
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#edede7',
            gap: 2,
            p: { xs: 2, sm: 4 },
          }}
        >
          <Box
            component={'img'}
            src='/static/home_page/sterling_cadillac/img_blackwing.png'
            sx={{ maxWidth: { xs: '50%', sm: '100%' } }}
          />

          <Box
            component={'img'}
            src='/static/home_page/sterling_cadillac/img_cadillac_cars.png'
            sx={{ maxWidth: '100%' }}
          />
          <Box sx={{ maxWidth: { xs: '100%', sm: '90%', lg: '70%' } }}>
            <Typography
              sx={{
                fontSize: typographyClamps.fontSize24,
                fontWeight: 'bold',
                mt: 1,
                alignSelf: 'start',
              }}
            >
              V- Series Performance
            </Typography>
            <Typography
              sx={{
                color: '#3a3a38',
                fontSize: typographyClamps.fontSize17,
                whiteSpace: 'pre-line',
                mt: 1,
              }}
            >
              <strong>
                Unleash the spirit of Cadillac performance with the V-Series
              </strong>{' '}
              — where precision engineering meets unmistakable luxury. The 2026
              Cadillac CT5-V commands attention in a striking electric blue
              finish, delivering bold styling, advanced technology, and a
              pulse-raising driving experience that sets it apart from the
              ordinary. Every detail, from its sculpted exterior to its
              driver-focused interior, is built for those who demand more from
              the road.{'\n\n'}
              Experience the power, sophistication, and presence of the V-Series
              — proudly showcased at Sterling Cadillac.
            </Typography>
          </Box>
        </Stack>

        <Grid
          container
          spacing={{xs: 1, sm: 5}}
          sx={{
            bgcolor: 'black',
            p: { xs: 0, sm: 2, md: 8 },
            display: 'flex',
            alignItems: 'center', 
            justifyContent: 'center'           
          }}
        >
          <Grid size={{ xs: 11, sm: 6.5 }}>
            <Box
              component={'img'}
              src='/static/home_page/sterling_cadillac/img_tampa_auto_show.png'
              sx={{
                // height: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4.5 }}>
            <Stack
              sx={{
                color: 'white',
                p: { xs: '10%', sm: 0 },                
                alignItems: 'center',
              }}
            >
              <Box component={'img'} src='/static/home_page/sterling_cadillac/tampa_auto_show_logo.png'/>

              {/* <Divider
                sx={{ background: '#631E1F', my: 1, width: 1, height: 2 }}
              /> */}

              <Typography
                sx={{
                  fontSize: typographyClamps.fontSize16,
                  lineHeight: 1.6,
                  mt: 2,
                  // mr: { xs: 0, sm: 2, md: 8 },
                  whiteSpace: 'pre-line',
                }}
              >
                <strong>Get ready to experience the future of luxury</strong>
                {'\n\n'}
                Join us at the <strong>2026 Auto Show</strong> in Tampa,
                Florida, and explore our latest Cadillac lineup up close. From
                bold design to cutting-edge innovation, see how our vehicles are
                redefining performance, comfort, and technology. Stop by our
                display, walk the lineup, and discover what’s next in automotive
                excellence.
              </Typography>

              <Button
                // variant='contained'
                sx={{
                  borderRadius: 1,
                  backgroundColor: AppColors.whiteColor,
                  // width: isMobile ? '120px' : '200px',
                  width: 'fit-content',
                  textTransform: 'none',
                  mt: 6,                  
                  px: { xs: 2, sm: 4 },
                  fontSize: typographyClamps.h6,
                  fontWeight: 400,
                  whiteSpace: 'pre-line', // This allows text to break at newline characters
                }}
                onClick={() => {                  
                }}
              >
                Get Tickets
              </Button>

              <Typography
                sx={{
                  fontSize: typographyClamps.fontSize16,
                  fontWeight: 'bold',
                  lineHeight: 1.6,
                  mt: 2,
                  
                }}
              >
                {' '}
                Coming June 23-28, 2026
              </Typography>
            </Stack>
          </Grid>
        </Grid>
                
      </Page>

      {videoUrl && (
        <VideoModel
          onClose={onClose}
          toggle={toggle}
          videoUrl={videoUrl ?? ''}
          title=''
        />
      )}
    </>
  );
};

export default Home;
